// Pro's brand and product pages: the brands area's own pages (`features/brands`) inside Pro's shell,
// so a product, a new product, a brand, its edit and a new brand all open, save and delete in Pro
// (spec ui-perf/ui-switch-classic-simple-pro, PR e4). Composed here: a feature never imports
// another; the composition root may.
import { createRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { EditBrand, NewBrand } from "../features/brands/BrandFormScreens";
import { BrandPage } from "../features/brands/BrandScreen";
import { NewProduct, ProductPage } from "../features/brands/ProductScreens";
import { studio2Route } from "../features/studio2/routes";
import { isUuid } from "../platform/routes";

const PRO = "/signed-in/teams/$teamId/brands/$brandId/studio/pro";

/** `pro/products/new`: a saved product opens its page in Pro. */
export const studio2NewProductRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "products/new",
  component: ProNewProduct,
});

/** `pro/products/$productId`: the product's page; a delete opens Pro's Products. */
export const studio2ProductRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "products/$productId",
  params: { parse: ({ productId }) => (isUuid(productId) ? { productId } : false) },
  component: ProProduct,
});

/** `pro/brands/new`: a new brand, under the brand Pro is on; a saved one opens its page. */
export const studio2NewBrandRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "brands/new",
  component: ProNewBrand,
});

/** `pro/brand`: the brand Pro is on, its page. */
export const studio2BrandRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "brand",
  component: ProBrand,
});

/** `pro/brand/edit`: the brand Pro is on, its edit. */
export const studio2EditBrandRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "brand/edit",
  component: ProEditBrand,
});

/**
 * The wrapper the forms' Pro look hangs on (`[data-pro-form]` in `features/brands/forms.module.css`,
 * `ImageField.module.css` and `ui/CentredPage.module.css`; spec ui-perf/pro-product-form): Classic's
 * pages, which draw the same forms, keep live's look.
 */
function ProForm({ children }: { children: ReactNode }) {
  return (
    <div data-pro-form style={{ height: "100%" }}>
      {children}
    </div>
  );
}

/** The back arrow to Pro's Products. */
function toProducts(teamId: string, brandId: string) {
  return (props: Record<string, unknown>) => (
    <Link {...props} to="/teams/$teamId/brands/$brandId/pro/products" params={{ teamId, brandId }} />
  );
}

/** The back arrow to Pro's Brands. */
function toBrands(teamId: string, brandId: string) {
  return (props: Record<string, unknown>) => (
    <Link {...props} to="/teams/$teamId/brands/$brandId/pro/brands" params={{ teamId, brandId }} />
  );
}

/** The link to the brand's page in Pro. */
function toBrand(teamId: string, brandId: string) {
  return (props: Record<string, unknown>) => (
    <Link {...props} to="/teams/$teamId/brands/$brandId/pro/brand" params={{ teamId, brandId }} />
  );
}

// Another brand starts each page anew, as Pro's other pages do (spec pro-studio-redesign, 31).

function ProNewProduct() {
  const { teamId, brandId } = useParams({ from: `${PRO}/products/new` });
  const navigate = useNavigate();
  return (
    <ProForm>
      <NewProduct
        key={`${teamId} ${brandId}`}
        teamId={teamId}
        brandId={brandId}
        back={toProducts(teamId, brandId)}
        onCreated={(productId) =>
          // Saved: the form's leave prompt has nothing to ask.
          navigate({
            to: "/teams/$teamId/brands/$brandId/pro/products/$productId",
            params: { teamId, brandId, productId },
            ignoreBlocker: true,
          })
        }
      />
    </ProForm>
  );
}

function ProProduct() {
  const { teamId, brandId, productId } = useParams({ from: `${PRO}/products/$productId` });
  const navigate = useNavigate();
  return (
    <ProForm>
      <ProductPage
        key={`${teamId} ${brandId} ${productId}`}
        teamId={teamId}
        brandId={brandId}
        productId={productId}
        back={toProducts(teamId, brandId)}
        onDeleted={() =>
          // Deleted: an edit left in its form has nothing left to save.
          navigate({
            to: "/teams/$teamId/brands/$brandId/pro/products",
            params: { teamId, brandId },
            replace: true,
            ignoreBlocker: true,
          })
        }
      />
    </ProForm>
  );
}

function ProNewBrand() {
  const { teamId, brandId } = useParams({ from: `${PRO}/brands/new` });
  const navigate = useNavigate();
  return (
    <ProForm>
      <NewBrand
        key={`${teamId} ${brandId}`}
        teamId={teamId}
        back={toBrands(teamId, brandId)}
        // As Classic's: the new brand's page, which Pro then is on.
        onCreated={(created) =>
          navigate({
            to: "/teams/$teamId/brands/$brandId/pro/brand",
            params: { teamId, brandId: created },
          })
        }
      />
    </ProForm>
  );
}

/** The brand Pro was on is gone: the team's way into Pro opens its first brand left. */
function useGoneHome(teamId: string) {
  const navigate = useNavigate();
  return () => navigate({ to: "/teams/$teamId/pro", params: { teamId }, replace: true });
}

function ProBrand() {
  const { teamId, brandId } = useParams({ from: `${PRO}/brand` });
  const onDeleted = useGoneHome(teamId);
  return (
    <BrandPage
      key={`${teamId} ${brandId}`}
      teamId={teamId}
      brandId={brandId}
      back={toBrands(teamId, brandId)}
      edit={(props) => (
        <Link
          {...props}
          to="/teams/$teamId/brands/$brandId/pro/brand/edit"
          params={{ teamId, brandId }}
        />
      )}
      onDeleted={onDeleted}
    />
  );
}

function ProEditBrand() {
  const { teamId, brandId } = useParams({ from: `${PRO}/brand/edit` });
  const navigate = useNavigate();
  const onDeleted = useGoneHome(teamId);
  return (
    <ProForm>
      <EditBrand
        key={`${teamId} ${brandId}`}
        teamId={teamId}
        brandId={brandId}
        back={toBrand(teamId, brandId)}
        onSaved={() =>
          navigate({ to: "/teams/$teamId/brands/$brandId/pro/brand", params: { teamId, brandId } })
        }
        onDeleted={onDeleted}
      />
    </ProForm>
  );
}
